/**
 * Barcode Input Utilities
 *
 * Client-side utilities for barcode and phone number validation
 * Used by placeholder-based input component
 * No scanner dependencies - pure validation functions
 */

/**
 * Radio Misr Barcode Format: R[YY][B][MM][C]M[CS1][CS2]
 * Example: R2431233M9838
 *
 * Strict validation regex - matches exact format
 */
const STRICT_BARCODE_REGEX = /^R\d{2}[1-9]\d{2}[1-9]\d{1}M\d{4}$/i;

/**
 * Phone number format: 01[0125][0-9]{8}
 * Example: 01012345678
 */
const PHONE_REGEX = /^01[0125][0-9]{8}$/;

/**
 * Validate if input matches Radio Misr barcode format
 * Poka-Yoke: Strict validation prevents invalid data entry
 */
export function isValidBarcodeFormat(input: string): boolean {
  if (!input || typeof input !== "string") {
    return false;
  }

  const cleaned = input.trim().toUpperCase();

  // Must match exact format: R + 2 digits + 1-9 + 2 digits + 1-9 + 1 digit + M + 4 digits
  return STRICT_BARCODE_REGEX.test(cleaned);
}

/**
 * Validate if input matches phone number format
 */
export function isValidPhoneFormat(input: string): boolean {
  if (!input || typeof input !== "string") {
    return false;
  }

  const cleaned = input.trim().replace(/[\s\-\(\)\+]/g, "");
  return PHONE_REGEX.test(cleaned);
}

/**
 * Normalize scanned input - remove any whitespace, special chars
 * Poka-Yoke: Clean input before validation
 */
export function normalizeScannedInput(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  // Remove all whitespace and common input artifacts
  return input
    .trim()
    .replace(/[\s\n\r\t]/g, "")
    .toUpperCase();
}

/**
 * Check if device is mobile/tablet
 * SSR-safe version
 */
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
}

/**
 * Format barcode with visual separators for display
 * Example: R2431233M9838 -> R 24 3 12 33 M 98 38
 */
export function formatBarcodeDisplay(barcode: string): string {
  if (!barcode || !isValidBarcodeFormat(barcode)) {
    return barcode;
  }

  const cleaned = normalizeScannedInput(barcode);
  // R2431233M9838 -> R 24 3 12 33 M 98 38
  const match = cleaned.match(/^R(\d{2})(\d)(\d{2})(\d{2})M(\d{2})(\d{2})$/i);

  if (!match) {
    return cleaned;
  }

  const [, yy, b, mm, c, cs1, cs2] = match;
  return `R ${yy} ${b} ${mm} ${c} M ${cs1} ${cs2}`;
}
